import { DAYS, WEST_ANAHEIM_HOSPITAL } from "../constants/timecard";

const MINUTES_PER_HOUR = 60;
const MINUTES_PER_DAY = 24 * MINUTES_PER_HOUR;
const QUARTER_HOUR_MINUTES = 15;
const CALLBACK_MINIMUM_MINUTES = 2 * MINUTES_PER_HOUR;

export function formatLocalDate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

export function addWeeks(date, weeks) {
  const result = new Date(date);
  result.setDate(result.getDate() + (weeks * 7));
  return result;
}

export function formatWeekRange(startDate) {
  const endDate = new Date(startDate);
  endDate.setDate(startDate.getDate() + 6);
  return `${startDate.toLocaleDateString("en-US", { month: "short", day: "numeric" })} - ${endDate.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`;
}

function parseDateForDisplay(value) {
  if (!value) {
    return null;
  }

  const stringValue = String(value).trim();
  const dateOnlyMatch = stringValue.match(/^(\d{4})-(\d{2})-(\d{2})$/);

  if (dateOnlyMatch) {
    const year = Number.parseInt(dateOnlyMatch[1], 10);
    const monthIndex = Number.parseInt(dateOnlyMatch[2], 10) - 1;
    const day = Number.parseInt(dateOnlyMatch[3], 10);
    const parsed = new Date(year, monthIndex, day);

    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  const parsed = new Date(stringValue);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function getDefaultWeekRange() {
  const now = new Date();
  const firstDay = new Date(now);
  firstDay.setDate(now.getDate() - now.getDay());
  const lastDay = new Date(firstDay);
  lastDay.setDate(firstDay.getDate() + 6);

  return [
    formatLocalDate(firstDay),
    formatLocalDate(lastDay)
  ];
}

export const createRegularRow = (day) => ({
  day,
  date: "",
  hospital: "",
  department: "",
  timeIn: "",
  breakStart: "",
  breakEnd: "",
  timeOut: "",
  totalHours: "",
  tookBreak: false,
  lateBreak: false,
  volFlex: false,
  flexed: false,
  notes: ""
});

export const createCallRow = (day) => ({
  day,
  date: "",
  hospital: "",
  department: "",
  startTime: "",
  endTime: "",
  totalHours: ""
});

// A callback row counts as data when any contractor-settable field is filled —
// keep buildTimecardPayload's row filter and useAutoSave's dirty check agreeing.
export const callbackRowHasData = (row) =>
  !!(row.timeInOne || row.timeOutOne || row.timeInTwo || row.timeOutTwo || row.totalHours || row.missedBreakAfterSix || row.notes);

export const createCallBackRow = (day) => ({
  day,
  timeInOne: "",
  timeOutOne: "",
  timeInTwo: "",
  timeOutTwo: "",
  totalHours: "",
  missedBreakAfterSix: false,
  notes: ""
});

export function createEmptyRows() {
  return {
    regularRows: DAYS.map(createRegularRow),
    callRows: DAYS.map(createCallRow),
    callbackRows: DAYS.map(createCallBackRow)
  };
}

export function getTotal(rows) {
  return rows.reduce((sum, row) => {
    const value = Number.parseFloat(row.totalHours);
    return Number.isFinite(value) ? sum + value : sum;
  }, 0);
}

export function formatHours(hours) {
  // Coerce defensively: callers may pass a raw row.totalHours, which can be ""
  // or undefined; Number(...) || 0 turns those (and NaN) into 0 rather than
  // throwing on .toFixed.
  const n = Number(hours) || 0;
  return `${parseFloat(n.toFixed(2))} hrs`;
}

function parseTimeValue(value) {
  if (!value) return null;

  const normalized = normalizeTimeString(value);
  if (!normalized) return null;

  const [hoursText, minutesText] = normalized.split(":");
  const hours = Number.parseInt(hoursText, 10);
  const minutes = Number.parseInt(minutesText, 10);

  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) {
    return null;
  }

  return (hours * MINUTES_PER_HOUR) + minutes;
}

function normalizeTimeString(value) {
  if (!value) return null;

  const trimmed = String(value).trim();
  const amPmMatch = trimmed.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM|am|pm)$/);
  if (amPmMatch) {
    let hours = Number.parseInt(amPmMatch[1], 10);
    const minutes = Number.parseInt(amPmMatch[2], 10);
    const seconds = amPmMatch[3] ? Number.parseInt(amPmMatch[3], 10) : 0;
    const period = amPmMatch[4].toUpperCase();

    if (period === "PM" && hours < 12) {
      hours += 12;
    }
    if (period === "AM" && hours === 12) {
      hours = 0;
    }

    return `${hours.toString().padStart(2, "0")}:${minutes
      .toString()
      .padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
  }

  const twentyFourHourMatch = trimmed.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
  if (twentyFourHourMatch) {
    const hours = Number.parseInt(twentyFourHourMatch[1], 10);
    const minutes = Number.parseInt(twentyFourHourMatch[2], 10);
    const seconds = twentyFourHourMatch[3]
      ? Number.parseInt(twentyFourHourMatch[3], 10)
      : 0;

    return `${hours.toString().padStart(2, "0")}:${minutes
      .toString()
      .padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
  }

  // Compact digit formats: "8"→"08:00", "17"→"17:00", "830"→"08:30", "1530"→"15:30"
  const compactDigits = trimmed.match(/^(\d{1,4})$/);
  if (compactDigits) {
    let hours, minutes;
    if (trimmed.length <= 2) {
      hours = Number.parseInt(trimmed, 10);
      minutes = 0;
    } else {
      const padded = trimmed.padStart(4, "0");
      hours = Number.parseInt(padded.slice(0, 2), 10);
      minutes = Number.parseInt(padded.slice(2, 4), 10);
    }
    if (hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59) {
      return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:00`;
    }
  }

  return null;
}

function getDurationInMinutes(startTime, endTime) {
  const startMinutes = parseTimeValue(startTime);
  const endMinutes = parseTimeValue(endTime);

  if (startMinutes === null || endMinutes === null) {
    return null;
  }

  const duration = endMinutes - startMinutes;
  return duration >= 0 ? duration : duration + MINUTES_PER_DAY;
}

function isWestAnaheimHospital(hospital) {
  return hospital === WEST_ANAHEIM_HOSPITAL;
}

function hospitalUsesExactTimes(hospitalOptions, hospitalName) {
  if (hospitalOptions?.length) {
    const hospital = hospitalOptions.find(h => h.name === hospitalName);
    if (hospital) return !!hospital.useExactShiftTimes;
  }
  return isWestAnaheimHospital(hospitalName);
}

function formatCalculatedHours(hours) {
  return Number.parseFloat(hours.toFixed(2)).toString();
}

function getRoundedWorkedHours(workedMinutes, useExact) {
  if (useExact) {
    return workedMinutes / MINUTES_PER_HOUR;
  }

  const roundedMinutes =
    Math.round(workedMinutes / QUARTER_HOUR_MINUTES) * QUARTER_HOUR_MINUTES;

  return roundedMinutes / MINUTES_PER_HOUR;
}

export function getTotalHourStep(hospital, hospitalOptions = []) {
  return hospitalUsesExactTimes(hospitalOptions, hospital) ? "0.01" : "0.25";
}

export function getRegularTotalHourStep(hospital, hospitalOptions = []) {
  return getTotalHourStep(hospital, hospitalOptions);
}

export function calculateRegularTotalHours(row, hospitalOptions = []) {
  const shiftMinutes = getDurationInMinutes(row.timeIn, row.timeOut);

  if (shiftMinutes === null) {
    return "";
  }

  const breakMinutes = getDurationInMinutes(row.breakStart, row.breakEnd) ?? 0;
  const workedMinutes = Math.max(shiftMinutes - breakMinutes, 0);

  return formatCalculatedHours(getRoundedWorkedHours(workedMinutes, hospitalUsesExactTimes(hospitalOptions, row.hospital)));
}

export function calculateCallTotalHours(row, hospitalOptions = []) {
  const workedMinutes = getDurationInMinutes(row.startTime, row.endTime);

  if (workedMinutes === null) {
    return "";
  }

  const effectiveMinutes = workedMinutes === 0 ? MINUTES_PER_DAY : workedMinutes;
  return formatCalculatedHours(getRoundedWorkedHours(effectiveMinutes, hospitalUsesExactTimes(hospitalOptions, row.hospital)));
}

export function getNetCallHours(grossHoursStr, callbackHoursStr) {
  if (!grossHoursStr) return "";
  const gross = Number.parseFloat(grossHoursStr) || 0;
  const callback = Number.parseFloat(callbackHoursStr) || 0;
  return formatCalculatedHours(Math.max(0, gross - callback));
}

export function calculateCallBackTotalHours(row, hospital = "", hospitalOptions = []) {
  const workedDurations = [
    getDurationInMinutes(row.timeInOne, row.timeOutOne),
    getDurationInMinutes(row.timeInTwo, row.timeOutTwo)
  ].filter((duration) => duration !== null);

  if (!workedDurations.length) {
    return "";
  }

  const workedMinutes = workedDurations.reduce(
    (totalMinutes, duration) => totalMinutes + Math.max(duration, CALLBACK_MINIMUM_MINUTES),
    0
  );

  return formatCalculatedHours(getRoundedWorkedHours(workedMinutes, hospitalUsesExactTimes(hospitalOptions, hospital)));
}

// Pure row-change transform for regular hours, shared by the contractor and admin
// dashboards. Returns the next rows array (callers own state updates / saving).
export function applyRegularRowChange(rows, index, field, value, hospitalOptions = []) {
  return rows.map((row, rowIndex) => {
    if (rowIndex !== index) {
      return row;
    }

    if (field === "hospital") {
      const updatedRow = {
        ...row,
        hospital: value,
        department: isDepartmentValidForHospital(hospitalOptions, value, row.department)
          ? row.department
          : ""
      };
      return { ...updatedRow, totalHours: calculateRegularTotalHours(updatedRow, hospitalOptions) };
    }

    if (field === "timeIn" || field === "breakStart" || field === "breakEnd" || field === "timeOut") {
      const updatedRow = { ...row, [field]: value };
      return { ...updatedRow, totalHours: calculateRegularTotalHours(updatedRow, hospitalOptions) };
    }

    return { ...row, [field]: value };
  });
}

// Pure row-change transform for call-back hours, shared by both dashboards.
// A time-field change also recomputes the matching call row's net hours, so this
// returns both arrays. When nothing else changes, the original callRows reference
// is returned unchanged so callers can tell whether call rows need persisting.
export function applyCallBackRowChange(callbackRows, callRows, index, field, value, hospitalOptions = []) {
  const isTimeField =
    field === "timeInOne" || field === "timeOutOne" || field === "timeInTwo" || field === "timeOutTwo";

  if (!isTimeField) {
    return {
      callbackRows: callbackRows.map((row, rowIndex) =>
        rowIndex === index ? { ...row, [field]: value } : row
      ),
      callRows
    };
  }

  const nextCallbackRows = callbackRows.map((row, rowIndex) => {
    if (rowIndex !== index) {
      return row;
    }
    const updatedRow = { ...row, [field]: value };
    const hospital = callRows.find((r) => r.day === updatedRow.day)?.hospital ?? "";
    return { ...updatedRow, totalHours: calculateCallBackTotalHours(updatedRow, hospital, hospitalOptions) };
  });

  const changedDay = callbackRows[index]?.day;
  const newCbHours = nextCallbackRows[index]?.totalHours ?? "";
  const nextCallRows = callRows.map((callRow) =>
    callRow.day === changedDay
      ? { ...callRow, totalHours: getNetCallHours(calculateCallTotalHours(callRow, hospitalOptions), newCbHours) }
      : callRow
  );

  return { callbackRows: nextCallbackRows, callRows: nextCallRows };
}

export function updateRow(setRows, index, field, value) {
  setRows((rows) =>
    rows.map((row, rowIndex) =>
      rowIndex === index ? { ...row, [field]: value } : row
    )
  );
}

function getTodayDayName() {
  return DAYS[new Date().getDay()];
}

function getCurrentTimeValue() {
  const now = new Date();
  return now.toTimeString().slice(0, 5);
}

export function clockIntoTodayRow(rows, onChange, fieldType, config, hintRowIndex) {
  const today = getTodayDayName();
  const currentTime = getCurrentTimeValue();
  const todayDate = formatLocalDate(new Date());

  const rowIndex = (hintRowIndex !== undefined && hintRowIndex >= 0)
    ? hintRowIndex
    : rows.findIndex((row) => row.day === today);
  if (rowIndex === -1) return null;

  const row = rows[rowIndex];
  let updatedRow = row;

  if (config.dateField && !updatedRow[config.dateField]) {
    onChange(rowIndex, config.dateField, todayDate);
    updatedRow = { ...updatedRow, [config.dateField]: todayDate };
  }

  if (config.sequence?.length) {
    const canSkip = config.skipToButtons?.includes(fieldType);
    const targetStep = canSkip
      ? config.sequence.find((step) => step.button === fieldType && !updatedRow[step.field])
      : (() => {
          const next = config.sequence.find((step) => !updatedRow[step.field]);
          return next?.button === fieldType ? next : null;
        })();

    if (targetStep) {
      onChange(rowIndex, targetStep.field, currentTime);
      updatedRow = { ...updatedRow, [targetStep.field]: currentTime };
      config.onFieldApplied?.(rowIndex, targetStep.field, updatedRow);
      return {
        day: updatedRow.day,
        rowIndex,
        field: targetStep.field,
        value: currentTime,
        row: updatedRow
      };
    }

    return null;
  }

  const fields = fieldType === "in" ? config.inFields : config.outFields;
  const firstEmptyField = fields.find((field) => !updatedRow[field]);

  if (firstEmptyField) {
    onChange(rowIndex, firstEmptyField, currentTime);
    updatedRow = { ...updatedRow, [firstEmptyField]: currentTime };
    config.onFieldApplied?.(rowIndex, firstEmptyField, updatedRow);
    return {
      day: updatedRow.day,
      rowIndex,
      field: firstEmptyField,
      value: currentTime,
      row: updatedRow
    };
  }

  return null;
}

export function parseTimeInput(value) {
  const normalized = normalizeTimeString(value);
  return normalized ? normalized.slice(0, 5) : null;
}

export function fmtStatusTime(hhmm, format) {
  if (!hhmm) return "";
  if (format === "12h") {
    const [h, m] = hhmm.split(":");
    const hours = parseInt(h, 10);
    return `${hours % 12 || 12}:${m} ${hours >= 12 ? "PM" : "AM"}`;
  }
  return hhmm;
}

export function formatApiDate(value) {
  if (!value) {
    return "";
  }

  const stringValue = String(value);
  return stringValue.slice(0, 10);
}

export function formatApiTime(value) {
  if (!value) {
    return "";
  }

  const stringValue = String(value);
  const [hours, minutes] = stringValue.split(":");
  if (hours === undefined || minutes === undefined) {
    return stringValue;
  }

  return `${hours.padStart(2, "0")}:${minutes.padStart(2, "0")}`;
}

export function formatDisplayDate(value) {
  if (!value) {
    return "";
  }

  const parsed = parseDateForDisplay(value);
  if (!parsed) {
    return String(value);
  }

  return new Intl.DateTimeFormat("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric"
  }).format(parsed);
}

export function formatShortMonthDay(value) {
  if (!value) {
    return "";
  }

  const parsed = parseDateForDisplay(value);
  if (!parsed) {
    return String(value);
  }

  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric"
  }).format(parsed);
}

export function getRowLabelDate(rowDate, weekStartDate, day) {
  if (rowDate) {
    return formatApiDate(rowDate);
  }

  if (!weekStartDate) {
    return "";
  }

  const dayIndex = DAYS.indexOf(day);
  if (dayIndex === -1) {
    return "";
  }

  const startDate = parseDateForDisplay(weekStartDate);
  if (!startDate) {
    return "";
  }

  const date = new Date(startDate);
  date.setDate(startDate.getDate() + dayIndex);
  return formatLocalDate(date);
}

function getProp(obj, camel, pascal) {
  if (!obj) return undefined;
  return obj[camel] ?? obj[pascal];
}

export function getHospitalIdByName(hospitalOptions, hospitalName) {
  const h = hospitalOptions?.find((hospital) => getProp(hospital, "name", "Name") === hospitalName);
  return getProp(h, "hospitalId", "HospitalId") ?? null;
}

export function getHospitalNameById(hospitalOptions, hospitalId) {
  const h = hospitalOptions?.find((hospital) => getProp(hospital, "hospitalId", "HospitalId") === hospitalId);
  return getProp(h, "name", "Name") ?? "";
}

export function getDepartmentIdByName(hospitalOptions, hospitalName, departmentName) {
  const hospital = hospitalOptions?.find((option) => getProp(option, "name", "Name") === hospitalName);
  const depts = getProp(hospital, "departments", "Departments") ?? [];
  const d = depts.find((dept) => getProp(dept, "name", "Name") === departmentName);
  return getProp(d, "departmentId", "DepartmentId") ?? null;
}

export function getDepartmentNameById(hospitalOptions, hospitalName, departmentId) {
  const hospital = hospitalOptions?.find((option) => getProp(option, "name", "Name") === hospitalName);
  const depts = getProp(hospital, "departments", "Departments") ?? [];
  const d = depts.find((dept) => getProp(dept, "departmentId", "DepartmentId") === departmentId);
  return getProp(d, "name", "Name") ?? "";
}

export function getHospitalDepartments(hospitalOptions, hospitalName) {
  const hospital = hospitalOptions?.find((option) => getProp(option, "name", "Name") === hospitalName);
  const depts = getProp(hospital, "departments", "Departments");
  const result = Array.isArray(depts) ? depts.filter(Boolean) : [];
  if (!result.length) {
    console.debug("[getHospitalDepartments] no departments", {
      hospitalName,
      hospitalFound: !!hospital,
      hospitalKeys: hospital ? Object.keys(hospital) : null,
      deptsType: typeof depts,
      deptsLength: Array.isArray(depts) ? depts.length : null,
      hospitalOptionsCount: hospitalOptions?.length ?? 0,
    });
  }
  return result;
}

export function isDepartmentValidForHospital(hospitalOptions, hospitalName, departmentName) {
  if (!departmentName) {
    return true;
  }

  return getHospitalDepartments(hospitalOptions, hospitalName)
    .some((department) => department.name === departmentName);
}

export function getCompletionTimeCounts(snapshot) {
  const completionTimes = new Map();

  const addRows = (prefix, rows, fields) => {
    (rows ?? []).forEach((row) => {
      const day = row?.day ?? "unknown";
      fields.forEach((field) => {
        const value = row?.[field] ?? "";
        if (!value) return;

        const key = `${prefix}:${day}:${value}`;
        completionTimes.set(key, (completionTimes.get(key) ?? 0) + 1);
      });
    });
  };

  addRows("regular", snapshot?.regularRows, ["timeOut"]);
  addRows("call", snapshot?.callRows, ["endTime"]);
  addRows("callback", snapshot?.callbackRows, ["timeOutOne", "timeOutTwo"]);

  return completionTimes;
}

export function hasNewCompletionTime(snapshot, savedSnapshotJson) {
  if (!savedSnapshotJson) {
    return false;
  }

  let savedSnapshot;
  try {
    savedSnapshot = JSON.parse(savedSnapshotJson);
  } catch {
    return false;
  }

  const currentCompletionTimes = getCompletionTimeCounts(snapshot);
  const savedCompletionTimes = getCompletionTimeCounts(savedSnapshot);

  for (const [key, count] of currentCompletionTimes) {
    if (count > (savedCompletionTimes.get(key) ?? 0)) {
      return true;
    }
  }

  return false;
}

export function buildTimecardPayload({
  timecardId,
  contractorId,
  weekStartDate,
  weekEndDate,
  status,
  attestationAccepted,
  signedByName,
  signatureValue,
  signedDate,
  regularRows,
  callRows,
  callbackRows,
  hospitalOptions
}) {
  const effectiveStatus = status || "draft";
  const formatTimeForApi = (value) => normalizeTimeString(value);

  const allRegularEntries = regularRows
    .filter((row) =>
      row.date || row.hospital || row.department || row.timeIn || row.timeOut || row.totalHours || row.tookBreak || row.volFlex || row.flexed || row.notes
    )
    .map((row) => ({
      dayOfWeek: DAYS.indexOf(row.day),
      workDate: row.date || null,
      hospitalId: getHospitalIdByName(hospitalOptions, row.hospital),
      departmentId: getDepartmentIdByName(hospitalOptions, row.hospital, row.department),
      timeIn: formatTimeForApi(row.timeIn),
      breakStart: formatTimeForApi(row.breakStart),
      breakEnd: formatTimeForApi(row.breakEnd),
      timeOut: formatTimeForApi(row.timeOut),
      totalHours: row.totalHours ? Number(row.totalHours) : null,
      tookBreak: !!row.tookBreak,
      volFlex: !!row.volFlex,
      flexed: !!row.flexed,
      notes: row.notes || null
    }));

  const allCallEntries = callRows
    .filter((row) =>
      row.date || row.hospital || row.department || row.startTime || row.endTime || row.totalHours
    )
    .map((row) => ({
      dayOfWeek: DAYS.indexOf(row.day),
      workDate: row.date || null,
      hospitalId: getHospitalIdByName(hospitalOptions, row.hospital),
      departmentId: getDepartmentIdByName(hospitalOptions, row.hospital, row.department),
      startTime: formatTimeForApi(row.startTime),
      endTime: formatTimeForApi(row.endTime),
      totalHours: row.totalHours ? Number(row.totalHours) : null
    }));

  const allCallbackEntries = callbackRows
    .filter(callbackRowHasData)
    .map((row) => {
      const matchingCallRow = callRows.find((callRow) => callRow.day === row.day);
      return {
        dayOfWeek: DAYS.indexOf(row.day),
        hospitalId: matchingCallRow
          ? getHospitalIdByName(hospitalOptions, matchingCallRow.hospital)
          : null,
        timeInOne: formatTimeForApi(row.timeInOne),
        timeOutOne: formatTimeForApi(row.timeOutOne),
        timeInTwo: formatTimeForApi(row.timeInTwo),
        timeOutTwo: formatTimeForApi(row.timeOutTwo),
        totalHours: row.totalHours ? Number(row.totalHours) : null,
        missedBreakAfterSix: !!row.missedBreakAfterSix,
        notes: row.notes || null
      };
    });

  return {
    timecardId: timecardId || 0,
    contractorId: Number(contractorId),
    weekStartDate: weekStartDate || null,
    weekEndDate: weekEndDate || null,
    status: effectiveStatus,
    attestationAccepted,
    signedByName: signedByName || null,
    signatureValue: signatureValue || null,
    signedDate: signedDate || null,
    submittedUtc: effectiveStatus === "submitted" ? new Date().toISOString() : null,
    regularEntries: allRegularEntries,
    callEntries: allCallEntries,
    callbackEntries: allCallbackEntries
  };
}

export function mapTimecardToFormState(timecard, hospitalOptions) {
  const regularEntriesByDay = new Map();
  for (const entry of (timecard.regularEntries ?? [])) {
    const list = regularEntriesByDay.get(entry.dayOfWeek) ?? [];
    regularEntriesByDay.set(entry.dayOfWeek, [...list, entry]);
  }
  const callEntriesByDay = new Map(timecard.callEntries?.map((entry) => [entry.dayOfWeek, entry]) ?? []);
  const callbackEntriesByDay = new Map(timecard.callbackEntries?.map((entry) => [entry.dayOfWeek, entry]) ?? []);

  const mapRegularEntry = (entry, day) => ({
    day,
    date: formatApiDate(entry?.workDate),
    hospital: getHospitalNameById(hospitalOptions, entry?.hospitalId) ?? "",
    department: getDepartmentNameById(hospitalOptions, getHospitalNameById(hospitalOptions, entry?.hospitalId), entry?.departmentId) ?? "",
    timeIn: formatApiTime(entry?.timeIn),
    breakStart: formatApiTime(entry?.breakStart),
    breakEnd: formatApiTime(entry?.breakEnd),
    timeOut: formatApiTime(entry?.timeOut),
    totalHours: entry?.totalHours?.toString() ?? "",
    tookBreak: entry?.tookBreak ?? false,
    lateBreak: entry?.lateBreak ?? false,
    volFlex: entry?.volFlex ?? false,
    flexed: entry?.flexed ?? false,
    notes: entry?.notes ?? ""
  });

  const regularRows = DAYS.map((day, index) => {
    const entries = regularEntriesByDay.get(index) ?? [];
    return mapRegularEntry(entries[0] ?? null, day);
  });

  for (const [dayIndex, entries] of regularEntriesByDay) {
    for (let i = 1; i < entries.length; i++) {
      regularRows.push(mapRegularEntry(entries[i], DAYS[dayIndex]));
    }
  }

  const callRows = DAYS.map((day, index) => {
    const entry = callEntriesByDay.get(index);
    const callbackEntry = callbackEntriesByDay.get(index);
    const hospitalName = getHospitalNameById(hospitalOptions, entry?.hospitalId) ?? "";
    const row = {
      day,
      date: formatApiDate(entry?.workDate),
      hospital: hospitalName,
      department: getDepartmentNameById(hospitalOptions, hospitalName, entry?.departmentId) ?? "",
      startTime: formatApiTime(entry?.startTime),
      endTime: formatApiTime(entry?.endTime),
    };
    const grossHours = calculateCallTotalHours(row, hospitalOptions);
    let callbackHours = "";
    if (callbackEntry) {
      const cbHospital = getHospitalNameById(hospitalOptions, callbackEntry.hospitalId) ?? hospitalName;
      const cbRow = {
        timeInOne: formatApiTime(callbackEntry.timeInOne),
        timeOutOne: formatApiTime(callbackEntry.timeOutOne),
        timeInTwo: formatApiTime(callbackEntry.timeInTwo),
        timeOutTwo: formatApiTime(callbackEntry.timeOutTwo),
      };
      callbackHours = calculateCallBackTotalHours(cbRow, cbHospital, hospitalOptions);
    }
    return {
      ...row,
      totalHours: getNetCallHours(grossHours, callbackHours) || entry?.totalHours?.toString() || ""
    };
  });

  const callbackRows = DAYS.map((day, index) => {
    const entry = callbackEntriesByDay.get(index);
    const callEntry = callEntriesByDay.get(index);
    const hospitalName = getHospitalNameById(hospitalOptions, callEntry?.hospitalId) ?? "";
    const row = {
      day,
      timeInOne: formatApiTime(entry?.timeInOne),
      timeOutOne: formatApiTime(entry?.timeOutOne),
      timeInTwo: formatApiTime(entry?.timeInTwo),
      timeOutTwo: formatApiTime(entry?.timeOutTwo),
      missedBreakAfterSix: entry?.missedBreakAfterSix ?? false,
      notes: entry?.notes ?? "",
    };
    return {
      ...row,
      totalHours: calculateCallBackTotalHours(row, hospitalName, hospitalOptions) || entry?.totalHours?.toString() || ""
    };
  });

  return {
    regularRows,
    callRows,
    callbackRows,
    weekStartDate: formatApiDate(timecard.weekStartDate),
    weekEndDate: formatApiDate(timecard.weekEndDate),
    status: timecard.status ?? "draft",
    attestationAccepted: timecard.attestationAccepted ?? false,
    signedByName: timecard.signedByName ?? "",
    signatureValue: timecard.signatureValue ?? "",
    signedDate: formatApiDate(timecard.signedDate)
  };
}
